import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

/**
 * AnalyticsDrilldownPanel — Sprint 3.7. Slide-in panel triggered by a chart
 * bucket click. Shows totals + top tenants for the bucket.
 */
export default function AnalyticsDrilldownPanel({ data, loading, error, onClose }) {
    const { t } = useTranslation();
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <>
            <div className="v3-anlx-backdrop" onClick={onClose} />
            <aside
                className="v3-anlx-panel"
                role="dialog"
                aria-modal="true"
                aria-label={t('Analytics drill-down')}
            >
                <div className="v3-anlx-panel-head">
                    <div>
                        <h3 className="v3-anlx-panel-title">{t('Drill-down')}</h3>
                        <div className="v3-anlx-panel-sub">{data?.label || t('Loading…')}</div>
                    </div>
                    <button
                        type="button"
                        className="v3-anlx-panel-close"
                        onClick={onClose}
                        aria-label={t('Close drill-down')}
                    >
                        <X size={14} />
                    </button>
                </div>

                <div className="v3-anlx-panel-body">
                    {loading && (
                        <>
                            <div className="v3-anlx-skel tile" />
                            <div className="v3-anlx-skel line long" />
                            <div className="v3-anlx-skel line long" />
                            <div className="v3-anlx-skel line short" />
                        </>
                    )}

                    {!loading && error && (
                        <div className="v3-anlx-error">{error}</div>
                    )}

                    {!loading && !error && data && (
                        <>
                            <div className="v3-anlx-panel-totals">
                                <KpiTile label={t('Sales')}        value={data.totals.sales}              fmt="money" />
                                <KpiTile label={t('Transactions')} value={data.totals.transactionCount}   fmt="int" />
                                <KpiTile label={t('ATV')}          value={data.totals.atv}                fmt="money" />
                                <KpiTile label={t('Rev Share')}    value={data.totals.revenueShareAmount} fmt="money" />
                            </div>

                            <h4 className="v3-anlx-table-title" style={{ marginBottom: 8 }}>
                                {t('Top tenants ({{count}})', { count: (data.rows || []).length })}
                            </h4>

                            {(data.rows || []).length === 0 ? (
                                <div className="v3-anlx-empty">{t('No tenant breakdown available for this bucket.')}</div>
                            ) : (
                                <table className="v3-anlx-table" role="table">
                                    <thead>
                                        <tr>
                                            <th>{t('Tenant')}</th>
                                            <th className="num">{t('Sales')}</th>
                                            <th className="num">{t('Tx')}</th>
                                            <th className="num">{t('ATV')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.rows.slice(0, 50).map((r, i) => (
                                            <tr key={`${r.groupByValue}-${i}`}>
                                                <td>{r.groupByValue || '—'}</td>
                                                <td className="num">{fmt(r.sales, 'money')}</td>
                                                <td className="num">{fmt(r.transactionCount, 'int')}</td>
                                                <td className="num">{fmt(r.atv, 'money')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}

function KpiTile({ label, value, fmt: kind }) {
    return (
        <div className="v3-card v3-anlx-kpi">
            <div className="v3-anlx-kpi-label">{label}</div>
            <div className="v3-anlx-kpi-value">{fmt(value, kind)}</div>
        </div>
    );
}

function fmt(v, kind) {
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) return '—';
    if (kind === 'int')   return Math.round(n).toLocaleString();
    if (kind === 'money') {
        if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
        if (Math.abs(n) >= 10_000)    return `${(n / 1_000).toFixed(1)}K`;
        return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return n.toLocaleString();
}
